// Redux Utils
import { useDispatch, useSelector } from "react-redux";

// React Utils
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

// Components
import Portada from "@/components/Perfil/Portada";
import InfoPerfil from "@/components/Perfil/InfoPerfil";
import Tab from "@/components/Tab";
import TicketCarousel from "@/components/TicketCarousel/TicketCarousel";
import TicketCardEventoPublico from "@/components/TicketCarousel/TicketCardEventoPublico";
import TicketCardReferralLink from "@/components/TicketCarousel/TicketCardReferralLink";

// Thunks
import {
  getUserInfo,
  getEventosConTicketsDeUsuario,
  getReferralLinksDeUsuario,
} from "@/features/user/userThunks";

// Utils
import { now } from "@/utils/dateHelpers";

export default function UsuarioPerfil() {
  // Obtener el UID de los parámetros de la ruta
  const { id } = useParams();

  // ========== REFS PARA EVITAR PETICIONES DUPLICADAS ==========
  const hasLoadedBasicData = useRef(false);
  const hasLoadedEventosData = useRef(false);

  // Estados locales para el manejo de la carga individual por sección
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);

  // Estados para las secciones de eventos con tickets
  const [eventosPasados, setEventosPasados] = useState([]);
  const [loadingEventosPasados, setLoadingEventosPasados] = useState(false);

  // Estados para las secciones de links de referido (si es embajador)
  const [referralLinksActivos, setReferralLinksActivos] = useState([]);
  const [referralLinksFinalizados, setReferralLinksFinalizados] = useState([]);
  const [loadingReferralLinks, setLoadingReferralLinks] = useState(false);

  // Estado para controlar qué tab está activa
  const [activeTab, setActiveTab] = useState(0);
  const [tabsDataLoaded, setTabsDataLoaded] = useState({
    eventos: false,
    misTickets: false,
  });

  // Redux state
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    info,
    isLoading: userLoading,
    error: userError,
  } = useSelector((state) => state.user);

  console.log("Rendering UsuarioPerfil with id:", id, "info:", info);
  const now = new Date().toISOString();

  // Cargar información básica del usuario al montar
  useEffect(() => {
    const loadBasicData = async () => {
      if (hasLoadedBasicData.current || !id) return;
      hasLoadedBasicData.current = true;

      console.log("🔵 Iniciando carga de información básica del usuario...");

      try {
        // 1. Cargar información del usuario
        console.log("📡 Cargando información del usuario...");
        await dispatch(getUserInfo({ uid: id })).unwrap();
        setUserInfoLoaded(true);
        console.log("✅ Información del usuario cargada");

        // 2. Cargar eventos pasados donde el usuario tiene tickets
        console.log("📡 Cargando eventos pasados...");
        setLoadingEventosPasados(true);
        const eventosResult = await dispatch(
          getEventosConTicketsDeUsuario({
            uid: id,
            filters: {
              eventoFechaHoraFinHasta: now,
              sortBy: "eventoFechaHoraInicio",
              sortOrder: "desc",
              page: 1,
              limit: 10,
            },
          }),
        ).unwrap();

        const eventosFormatted =
          eventosResult?.eventos?.map((evento) => ({
            uid: evento.uid,
            nombre: evento.nombre,
            img: evento.img,
            fechaHoraInicio: evento.fechaHoraInicio,
            fechaHoraFin: evento.fechaHoraFin,
            direccion: evento.direccion,
            zonaHoraria: evento.zonaHoraria,
            published: evento.published,
          })) || [];

        console.log(
          "✅ Eventos pasados obtenidos del backend:",
          eventosFormatted,
        );

        setEventosPasados(eventosFormatted);
        setLoadingEventosPasados(false);
        setTabsDataLoaded((prev) => ({ ...prev, misTickets: true }));
        console.log("✅ Eventos pasados cargados:", eventosFormatted.length);
      } catch (error) {
        console.error("❌ Error al cargar datos básicos:", error);
        setUserInfoLoaded(true);
        setLoadingEventosPasados(false);
      }
    };

    loadBasicData();
  }, [dispatch, id]);

  // Cargar eventos de embajador automáticamente cuando info esté disponible y sea embajador
  useEffect(() => {
    if (userInfoLoaded && info?.perEmbajador === 1 && !tabsDataLoaded.eventos) {
      console.log(
        "🔵 Usuario es embajador, cargando eventos automáticamente...",
      );
      loadEventosData();
    }
  }, [userInfoLoaded, info?.ambassadorActive]);

  // Función para cargar eventos (links de referido) si es embajador
  const loadEventosData = async () => {
    if (hasLoadedEventosData.current || !info?.ambassadorActive) return;
    hasLoadedEventosData.current = true;

    console.log("🔵 Iniciando carga de links de referido...");
    setLoadingReferralLinks(true);

    try {
      // Cargar links activos
      console.log("📡 Cargando links de referido activos...");
      const activosResult = await dispatch(
        getReferralLinksDeUsuario({
          uid: id,
          filters: {
            eventoEstado: "activo",
            eventoFechaHoraInicioDesde: now,
            sortBy: "eventoFechaHoraInicio",
            sortOrder: "asc",
            page: 1,
            limit: 10,
          },
        }),
      ).unwrap();

      console.log("✅ Links activos obtenidos del backend:", activosResult);
      setReferralLinksActivos(activosResult?.links || []);
      console.log(
        "✅ Links activos cargados:",
        activosResult?.links?.length || 0,
      );

      // Cargar links finalizados
      console.log("📡 Cargando links de referido finalizados...");
      const finalizadosResult = await dispatch(
        getReferralLinksDeUsuario({
          uid: id,
          filters: {
            eventoEstado: "activo",
            eventoFechaHoraFinHasta: now,
            sortBy: "eventoFechaHoraFin",
            sortOrder: "desc",
            page: 1,
            limit: 10,
          },
        }),
      ).unwrap();

      setReferralLinksFinalizados(finalizadosResult?.links || []);
      console.log(
        "✅ Links finalizados cargados:",
        finalizadosResult?.links?.length || 0,
      );

      setTabsDataLoaded((prev) => ({ ...prev, eventos: true }));
    } catch (error) {
      console.error("❌ Error al cargar links de referido:", error);
    } finally {
      setLoadingReferralLinks(false);
    }
  };

  // Manejar cambio de tab
  const handleTabChange = (index) => {
    console.log("🔄 Cambiando a tab:", index);
    setActiveTab(index);

    // Determinar qué tab se activó basándonos en el index y la configuración de tabs
    const isAmbassador = info?.perEmbajador === 1;

    if (isAmbassador) {
      // Si es embajador: index 0 = Eventos, index 1 = Mis Tickets
      if (index === 0 && !tabsDataLoaded.eventos) {
        loadEventosData();
      }
    }
    // Si no es embajador, solo tiene Mis Tickets (index 0), que ya se carga al inicio
  };

  // Estados de carga individuales para cada sección
  const isLoadingPortada = !userInfoLoaded || userLoading;
  const isLoadingInfoPerfil = !userInfoLoaded || userLoading;

  // Construir tabs dinámicamente según si es embajador
  const tabs = [];

  // Si es embajador activo, mostrar tab de Eventos primero
  if (info?.perEmbajador === 1) {
    tabs.push({
      name: "Eventos",
      icon: "fa-solid fa-calendar-days",
      current: activeTab === 0,
    });
  }

  // Siempre mostrar tab de Mis Tickets
  tabs.push({
    name: "Mis Tickets",
    icon: "fa-solid fa-ticket",
    current: info?.perEmbajador === 1 ? activeTab === 1 : activeTab === 0,
  });

  const tabInfo = {
    title: "",
    description: "",
    layout: "horizontal",
    textColor: "text-white",
    textColorHover: "hover:text-aw-green",
    textColorActive: "text-aw-green",
    barTabActive: "bg-aw-green",
    bgColor: "bg-aw-bluedark",
    bgColorHover: "hover:bg-aw-bluedark-75",
    bgColorActive: "bg-aw-bluedark-50",
    borderColor: "border-aw-bluedark-50",
    borderColorActive: "aw-green",
    tabs: tabs,
  };

  // Construir contenido de las pestañas dinámicamente para garantizar sincronización con tabs
  const tabContents = [];

  // Si es embajador, agregar contenido de Eventos (índice 0)
  if (info?.perEmbajador === 1) {
    tabContents.push(
      <div
        key="eventos"
        className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]"
      >
        {loadingReferralLinks ? (
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <div className="flex gap-4 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="min-w-[200px] max-w-[200px]">
                  <Skeleton height={208} className="mb-2" borderRadius={8} />
                  <div className="px-2 py-1">
                    <Skeleton height={20} className="mb-1" />
                    <Skeleton height={14} />
                  </div>
                  <Skeleton height={36} className="mt-2" />
                </div>
              ))}
            </div>
          </SkeletonTheme>
        ) : (
          <>
            {/* Eventos Activos */}
            <div>
              <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                Eventos Activos
              </h3>
              {referralLinksActivos.length > 0 ? (
                <TicketCarousel
                  tickets={referralLinksActivos}
                  TicketItem={TicketCardReferralLink}
                  sectionType="referralLinksActivos"
                  uid={id}
                  onLoadMore={(newLinks) => {
                    console.log(
                      "📥 onLoadMore Activos - Links recibidos:",
                      newLinks,
                    );
                    setReferralLinksActivos((prev) => [...prev, ...newLinks]);
                  }}
                />
              ) : (
                <p className="text-gray-400 text-center py-8">
                  No hay eventos activos en este momento
                </p>
              )}
            </div>

            {/* Eventos Finalizados */}
            <div>
              <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                Eventos Finalizados
              </h3>
              {referralLinksFinalizados.length > 0 ? (
                <TicketCarousel
                  tickets={referralLinksFinalizados}
                  TicketItem={TicketCardReferralLink}
                  sectionType="referralLinksFinalizados"
                  uid={id}
                  onLoadMore={(newLinks) => {
                    console.log(
                      "📥 onLoadMore Finalizados - Links recibidos:",
                      newLinks,
                    );
                    setReferralLinksFinalizados((prev) => [
                      ...prev,
                      ...newLinks,
                    ]);
                  }}
                />
              ) : (
                <p className="text-gray-400 text-center py-8">
                  No hay eventos finalizados
                </p>
              )}
            </div>
          </>
        )}
      </div>,
    );
  }

  // Siempre agregar contenido de Mis Tickets (índice 0 si no es embajador, índice 1 si es embajador)
  tabContents.push(
    <div
      key="misTickets"
      className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]"
    >
      {loadingEventosPasados ? (
        <div className="text-center text-white">
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <Skeleton height={30} width={200} className="mb-4 mx-auto" />
            <div className="flex gap-4 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="min-w-[200px] max-w-[200px]">
                  <Skeleton height={208} className="mb-2" borderRadius={8} />
                  <div className="px-2 py-1">
                    <Skeleton height={20} className="mb-1" />
                    <Skeleton height={14} />
                  </div>
                  <Skeleton height={36} className="mt-2" />
                </div>
              ))}
            </div>
          </SkeletonTheme>
        </div>
      ) : (
        <div>
          <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
            Eventos Pasados
          </h3>
          {eventosPasados.length > 0 ? (
            <TicketCarousel
              tickets={eventosPasados}
              TicketItem={TicketCardEventoPublico}
              sectionType="eventosPasados"
              uid={id}
              onLoadMore={(newEventos) => {
                console.log(
                  "📥 onLoadMore Eventos Pasados - Eventos recibidos:",
                  newEventos,
                );
                setEventosPasados((prev) => [...prev, ...newEventos]);
              }}
            />
          ) : (
            <p className="text-gray-400 text-center py-8">
              No hay eventos pasados para mostrar
            </p>
          )}
        </div>
      )}
    </div>,
  );

  console.log("[referralLinksActivos]:", referralLinksActivos);
  console.log("[tabContents length]:", tabContents.length);
  console.log("[tabs length]:", tabs.length);

  return (
    <>
      <div className="w-full max-w-5xl mx-auto relative">
        {/* Portada */}
        {isLoadingPortada ? (
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <Skeleton height={320} className="w-full rounded-b-3xl" />
          </SkeletonTheme>
        ) : (
          <Portada info={info} />
        )}
      </div>

      <div
        className={`w-full rounded-t-[8%] bg-aw-blueblack text-aw flex flex-col items-center pt-20 -mt-20 sm:-mt-[5.5rem] ${
          info?.empresas?.length > 0 ? "" : "min-h-screen"
        }`}
      >
        {/* Content Perfil */}
        <div className="w-full max-w-5xl mx-auto relative sm:mb-8">
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 px-2 z-10 relative">
            {/* Información del Perfil */}
            {isLoadingInfoPerfil ? (
              <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                <div className="w-full sm:w-1/2 flex flex-row items-end sm:items-center gap-4 px-2 z-10 relative">
                  <div className="relative">
                    <Skeleton
                      circle={true}
                      height={112}
                      width={112}
                      className="rounded-full border-4 border-white object-cover shadow-lg bg-aw-bluegray"
                    />
                  </div>
                  <div className="flex flex-col gap-2 mb-6">
                    <Skeleton height={24} width={200} borderRadius={8} />
                    <Skeleton height={20} width={150} borderRadius={8} />
                  </div>
                </div>
              </SkeletonTheme>
            ) : (
              <InfoPerfil info={info} />
            )}
          </div>
        </div>
        {isLoadingPortada && (
          <div className="w-full px-4">
            <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
              <div className="flex w-full gap-2">
                <Skeleton height={44} width={300} borderRadius={8} />
              </div>
            </SkeletonTheme>
          </div>
        )}

        {/* Tabs */}
        <div className="w-full pb-8">
          <Tab tabInfo={tabInfo} onTabChange={handleTabChange}>
            {tabContents}
          </Tab>
        </div>
      </div>
    </>
  );
}
